/**
 * Goals Service (Epic 1)
 * 
 * Business logic layer for Goals API
 * Handles all goal-related operations with proper error handling
 */

import { apiService } from './api.service';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  Goal,
  CreateGoalRequest,
  CreateGoalResponse,
  UpdateGoalRequest,
  UpdateGoalResponse,
  GoalListResponse,
  GoalProgressData,
  GoalSummaryData,
} from '@/types/goals';

/**
 * Goals Service class
 */
class GoalsService {
  /**
   * List goals (optional filter by status)
   */
  async listGoals(status?: 'active' | 'completed' | 'replaced'): Promise<Goal[]> {
    const endpoint = status 
      ? `${API_ENDPOINTS.GOALS.LIST}?status=${status}` 
      : API_ENDPOINTS.GOALS.LIST;

    const response = await apiService.get<GoalListResponse | Goal[]>(endpoint);
    
    // Handle both array and object responses
    return Array.isArray(response) ? response : (response as GoalListResponse).data ?? [];
  }

  /**
   * Get current active goal
   * Returns null when no active goal exists (404)
   */
  async getCurrentGoal(): Promise<Goal | null> {
    try {
      const response = await apiService.get<Goal>(API_ENDPOINTS.GOALS.CURRENT);
      return response;
    } catch (error: unknown) {
      // Return null for 404 (no active goal)
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get current goal progress (Epic 4: Dynamic Progress)
   * Returns null when no active goal exists (404)
   */
  async getCurrentGoalProgress(): Promise<GoalProgressData | null> {
    try {
      const response = await apiService.get<GoalProgressData>(
        API_ENDPOINTS.GOALS.CURRENT_PROGRESS
      );
      return response;
    } catch (error: unknown) {
      // Return null for 404 (no active goal)
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get goal by ID
   */
  async getGoalById(id: string): Promise<Goal> {
    return apiService.get<Goal>(API_ENDPOINTS.GOALS.BY_ID(id));
  }

  /**
   * Get goal completion summary (stats, streak, daily pages)
   */
  async getGoalSummary(goalId: string): Promise<GoalSummaryData> {
    return apiService.get<GoalSummaryData>(API_ENDPOINTS.GOALS.SUMMARY(goalId));
  }

  /**
   * Manually complete a goal (wrap up overdue)
   */
  async completeGoal(goalId: string): Promise<Goal> {
    return apiService.post<Goal>(API_ENDPOINTS.GOALS.COMPLETE(goalId), undefined);
  }

  /**
   * Create goal
   * Returns full response including replaced_goal_id and warnings
   */
  async createGoal(data: CreateGoalRequest): Promise<CreateGoalResponse> {
    return apiService.postFull<CreateGoalResponse, CreateGoalRequest>(
      API_ENDPOINTS.GOALS.CREATE,
      data
    );
  }

  /**
   * Update goal (partial update)
   * Returns full response including warnings
   */
  async updateGoal(id: string, data: UpdateGoalRequest): Promise<UpdateGoalResponse> {
    return apiService.patchFull<UpdateGoalResponse, UpdateGoalRequest>(
      API_ENDPOINTS.GOALS.UPDATE(id),
      data
    );
  }

  /**
   * Delete goal
   */
  async deleteGoal(id: string): Promise<void> {
    await apiService.delete<void>(API_ENDPOINTS.GOALS.DELETE(id));
  }
}

// Export singleton instance
export const goalsService = new GoalsService();
